import { useEffect, useRef, useState } from 'react';
import {
    getVideoClient,
    getVideoStream,
    onActiveShareChange,
} from '../../../hooks/useVideoSession';

/**
 * Superficie de pantalla compartida.
 * - video/canvas locales: requeridos por startShareScreen(element)
 * - remoto: attachShareView debe ir dentro de <video-player-container>
 */
export const VideoShareStage = ({ onStopShare, onShareStateChange }) => {
    const remoteRef = useRef(null);
    const localVideoHostRef = useRef(null);
    const attachedRef = useRef({ userId: null, element: null, container: null });
    const [shareUserId, setShareUserId] = useState(null);
    const [selfSharing, setSelfSharing] = useState(false);

    useEffect(() => onActiveShareChange(setShareUserId), []);

    useEffect(() => {
        const client = getVideoClient();
        const me = client?.getCurrentUserInfo?.();
        const isSelf = Boolean(me && shareUserId != null && me.userId === shareUserId);
        setSelfSharing(isSelf);
        onShareStateChange?.({
            active: shareUserId != null,
            selfSharing: isSelf,
            shareUserId,
        });
    }, [shareUserId, onShareStateChange]);

    useEffect(() => {
        let cancelled = false;

        const clearRemote = async () => {
            const stream = getVideoStream();
            const prev = attachedRef.current;
            if (prev.userId != null && stream) {
                try {
                    const detached = await stream.detachShareView(prev.userId);
                    if (Array.isArray(detached)) detached.forEach((el) => el?.remove?.());
                    else detached?.remove?.();
                } catch {
                    // ignore
                }
            }
            if (prev.container?.parentNode) {
                prev.container.parentNode.removeChild(prev.container);
            }
            if (remoteRef.current) remoteRef.current.innerHTML = '';
            attachedRef.current = { userId: null, element: null, container: null };
        };

        const syncRemoteShare = async () => {
            const stream = getVideoStream();
            const client = getVideoClient();
            const me = client?.getCurrentUserInfo?.();
            const isSelf = Boolean(me && shareUserId != null && me.userId === shareUserId);

            const prev = attachedRef.current;
            if (prev.userId != null && prev.userId !== shareUserId) {
                await clearRemote();
            }

            // Si el profesor congela, Zoom pausa el share y la vista conserva el
            // último fotograma: no hay que desadjuntar ni volver a adjuntar.
            if (cancelled || shareUserId == null || isSelf
                || !stream || !remoteRef.current) {
                return;
            }

            if (attachedRef.current.userId === shareUserId) return;

            const delays = [0, 400, 1200, 2500];
            for (let i = 0; i < delays.length; i += 1) {
                if (delays[i]) {
                    await new Promise((resolve) => setTimeout(resolve, delays[i]));
                }
                if (cancelled || !remoteRef.current) return;
                if (attachedRef.current.userId === shareUserId) return;

                try {
                    const container = document.createElement('video-player-container');
                    container.className = 'vc-share__player';
                    remoteRef.current.innerHTML = '';
                    remoteRef.current.appendChild(container);

                    const element = await stream.attachShareView(shareUserId);
                    if (cancelled || !element || element?.errorCode) {
                        if (container.parentNode) container.parentNode.removeChild(container);
                        continue;
                    }
                    container.appendChild(element);
                    attachedRef.current = { userId: shareUserId, element, container };
                    return;
                } catch (err) {
                    console.warn('[VideoSDK] attachShareView:', err?.message || err);
                }
            }
        };

        void syncRemoteShare();

        return () => {
            cancelled = true;
            void clearRemote();
        };
    }, [shareUserId]);

    useEffect(() => () => {
        const stream = getVideoStream();
        const prev = attachedRef.current;
        if (stream && prev.userId != null) {
            void stream.detachShareView(prev.userId).catch(() => {});
        }
        if (prev.container?.parentNode) {
            prev.container.parentNode.removeChild(prev.container);
        }
        attachedRef.current = { userId: null, element: null, container: null };
    }, []);

    // Mueve el <video>/<canvas> local dentro del área de share (no sobre los participantes).
    useEffect(() => {
        const host = localVideoHostRef.current;
        const video = document.getElementById('vc-share-self-video');
        const canvas = document.getElementById('vc-share-self-canvas');
        if (!host) return undefined;
        if (video && video.parentElement !== host) host.appendChild(video);
        if (canvas && canvas.parentElement !== host) host.appendChild(canvas);
        return undefined;
    }, [selfSharing]);

    const active = shareUserId != null;

    return (
        <div className={`vc-share ${active ? 'is-active' : ''}`} aria-hidden={!active}>
            <div
                ref={localVideoHostRef}
                className="vc-share__local-host"
                style={{ display: selfSharing ? 'flex' : 'none' }}
            >
                <video
                    id="vc-share-self-video"
                    className={`vc-share-el ${selfSharing ? 'is-visible' : ''}`}
                    playsInline
                    muted
                />
                <canvas
                    id="vc-share-self-canvas"
                    className="vc-share-el"
                    width={1920}
                    height={1080}
                />
            </div>

            <div
                ref={remoteRef}
                className="vc-share__remote"
                style={{ display: active && !selfSharing ? 'flex' : 'none' }}
            />

            {active && (
                <div className="vc-share__chrome">
                    <span className="vc-share__label">
                        {selfSharing ? 'Estás compartiendo' : 'Pantalla compartida'}
                    </span>
                    {selfSharing && (
                        <button
                            type="button"
                            className="vc-share__stop"
                            onClick={() => onStopShare?.()}
                        >
                            Dejar de compartir
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};
